import {Component, ElementRef, HostListener, effect, input, output, viewChild} from '@angular/core';
import {DeviceCredentialSummary} from '../shared/device-credential.models';

/**
 * Confirms that a register credential is permanently retired (#469, step 7).
 *
 * ### Why a modal, not the row
 *
 * Following the style guide's destructive-confirmation rule: an inline confirmation swaps a one-line action
 * for a sentence plus two buttons **inside the same table cell**, so the row grows and every row below it
 * shifts under the cursor exactly as the confirmation appears. That is the mis-click shape that once
 * confirmed the wrong row during live testing, and revoking cannot be undone — a revoked credential is never
 * rotated back to life, only replaced.
 *
 * ### It names what actually breaks, and what does not
 *
 * Revoking stops this credential **acquiring new tokens**. A till already signed in keeps working until its
 * own token expires; nothing here ends a session. Saying only "the credential will be revoked" would leave
 * an administrator believing they had just closed a venue's registers — or, worse, believing they had, and
 * walking away from a till that is still trading.
 *
 * ### Dismissible by backdrop and Escape — unlike {@link CredentialSecretDialogComponent}
 *
 * That dialog refuses every accidental exit, because closing it destroys a password that cannot be shown
 * again. Cancelling a revoke costs nothing — the credential is untouched either way — so a stray click
 * outside or an `Escape` press is an ordinary **cancel**, the safe direction. The asymmetry between the two
 * dialogs is deliberate; do not "fix" one to match the other.
 */
@Component({
  selector: 'device-credential-revoke-dialog',
  standalone: true,
  template: `
    <!-- Backdrop click cancels (see the class doc: safe here, unlike the password dialog). -->
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <div class="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg
                  sm:rounded-2xl"
           role="alertdialog" aria-modal="true" aria-labelledby="revoke-credential-heading"
           aria-describedby="revoke-credential-body" (click)="$event.stopPropagation()">
        <div class="space-y-4 p-4 sm:p-6">

          <h2 id="revoke-credential-heading" class="text-xl font-bold tracking-tight text-gray-900">
            Zugangsdaten sperren?
          </h2>

          <div id="revoke-credential-body" class="space-y-3 text-sm text-gray-700">
            <p>
              Bezeichnung: <span class="font-semibold text-gray-900">{{ credential().name }}</span>
            </p>
            <p>
              Kennung:
              <span class="font-mono text-xs text-gray-500">{{ credential().clientId }}</span>
            </p>

            <p class="rounded-2xl bg-red-50 px-4 py-3 font-semibold text-red-700" role="alert">
              Mit dieser Kennung kann sich danach keine Kasse mehr anmelden.
            </p>

            <p>
              Bereits angemeldete Kassen bleiben angemeldet. Ihre Sitzung endet erst, wenn ihr eigenes Token
              abläuft. Um eine Kasse sofort auszusperren, sperren Sie das Gerät unter <b>Geräte</b>.
            </p>

            <p>
              Sperren lässt sich nicht rückgängig machen. Stellen Sie danach neue Zugangsdaten aus.
            </p>
          </div>

          <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
            <!-- Cancel is the low-emphasis control and holds focus by default: the safe answer to a
                 destructive prompt should be the one a stray Enter press lands on. -->
            <button type="button" #cancelButton (click)="cancel()"
                    class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                           py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                           hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Abbrechen
            </button>
            <button type="button" (click)="confirm()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-red-600 px-5 py-2.5 text-sm
                           font-semibold text-white shadow-lg shadow-red-100 transition-all duration-200
                           hover:bg-red-700 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed
                           disabled:opacity-30 disabled:grayscale">
              Ja, sperren
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class RevokeCredentialDialogComponent {
  /** What is being revoked — set once; the parent mounts a fresh instance per confirmation. */
  readonly credential = input.required<DeviceCredentialSummary>();

  /** Disables the confirm button while the revoke call is in flight. */
  readonly busy = input(false);

  /** Confirmed — the parent makes the call and closes the dialog on the answer. */
  readonly confirmed = output<void>();

  /** Cancel button, backdrop click, or Escape — all the same outcome: nothing changes. */
  readonly cancelled = output<void>();

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  constructor() {
    // Put the caret on Abbrechen as soon as the dialog appears — the safe default for a destructive prompt.
    effect(() => {
      const button = this.cancelButton()?.nativeElement;
      if (button) button.focus();
    });
  }

  protected confirm(): void {
    if (this.busy()) return;
    this.confirmed.emit();
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
